"use client";

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { certificatesApi } from "@/lib/api/services/certificates.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/shared/empty-state";
import { Award, Download, ArrowRight } from "lucide-react";
import Link from "next/link";

interface Certificate {
  id: string;
  name: string;
  issuer: string;
  issuedAt: string;
  expiresAt?: string;
  status: string;
  type?: string;
}

export default function CertificatesPage() {
  const [typeFilter, setTypeFilter] = useState("all");

  const { data: certificates, isLoading } = useQuery({
    queryKey: ["certificates", typeFilter],
    queryFn: async () => {
      const data = await certificatesApi.listMine({ type: typeFilter !== "all" ? typeFilter : undefined });
      return ((data as unknown as { data?: Certificate[] }).data ?? []) as Certificate[];
    },
  });

  const filtered = typeFilter === "all" ? (certificates ?? []) : (certificates ?? []).filter((c) => c.type === typeFilter);

  const typeFilters = [
    { value: "all", label: "All Types" },
    { value: "professional", label: "Professional" },
    { value: "compliance", label: "Compliance" },
    { value: "learning", label: "Learning" },
  ];

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="space-y-1">
          <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground">Certificates</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">Your earned credentials and certifications.</p>
        </div>
        <Button asChild size="sm">
          <Link href="/certificates">View All</Link>
        </Button>
      </div>

      <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground" aria-label="Filter by type">
        {typeFilters.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
      </select>

      {isLoading ? (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}><CardContent className="p-4"><div className="h-4 w-2/3 bg-muted/10 rounded animate-pulse" /><div className="mt-2 h-3 w-1/2 bg-muted/10 rounded animate-pulse" /></CardContent></Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No certificates found"
          description="Complete courses to earn certificates."
          variant="guide"
          action={
            <Button asChild>
              <Link href="/learning/courses">
                <span className="mr-2">Browse Courses</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          }
        />
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((cert) => (
            <Card key={cert.id} className="transition-colors hover:border-border/80">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <Award className="h-6 w-6 text-primary" />
                  </div>
                  <span className={`h-5 px-2 text-[0.6875rem] font-medium rounded-full border ${cert.status === "active" ? "bg-primary/10 text-primary border-primary/20" : "bg-muted/10 text-muted-foreground border-border"}`}>{cert.status}</span>
                </div>
                <h3 className="mt-4 font-semibold text-foreground text-sm">{cert.name}</h3>
                <p className="text-xs text-muted-foreground">{cert.issuer}</p>
                <p className="mt-2 text-xs text-muted-foreground">Issued {new Date(cert.issuedAt).toLocaleDateString()}</p>
                {cert.expiresAt && <p className="text-xs text-muted-foreground">Expires {new Date(cert.expiresAt).toLocaleDateString()}</p>}
                <Button variant="outline" size="sm" className="mt-4 w-full"><Download className="mr-2 h-4 w-4" /> Download</Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
